import type { MeetingListItemDTO } from '@copilot/shared';
import { MeetingStatusSchema, OutcomeSchema, PlatformSchema, SentimentLevelSchema } from '@copilot/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router';
import { clientsApi } from '../../api/clients';
import { meetingsApi } from '../../api/meetings';
import { teamsApi, usersApi } from '../../api/users';
import { useUser } from '../../auth/AuthProvider';
import {
  Avatar,
  Badge,
  Button,
  Card,
  ClientPicker,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  PageHeader,
  ListSkeleton,
  Pagination,
  Select,
  SortableTh,
  Table,
  Td,
  Th,
  Tr,
  useDebounced,
} from '../../components';
import { cn } from '../../lib/cn';
import { formatDateTime, formatDuration } from '../../lib/format';
import { OUTCOME_LABELS, PLATFORM_LABELS, SENTIMENT_LABELS, STATUS_LABELS } from '../../lib/labels';
import {
  PAGE_SIZES,
  clearMeetingFilters,
  hasActiveFilters,
  meetingFiltersToApiQuery,
  meetingFiltersToSearchParams,
  parseMeetingFilters,
  toggleSort,
  updateMeetingFilters,
  type MeetingFilters,
  type MeetingSortKey,
} from '../../lib/meetingFilters';
import { MeetingStatusBadge, OutcomeBadge, SentimentBadge } from './badges';

/**
 * Meetings list. `scope="mine"` is the rep-facing "My meetings" view
 * (no rep/team filters, no export).
 */
export function MeetingsPage({ scope }: { scope: 'visible' | 'mine' }) {
  const user = useUser();
  const [sp, setSp] = useSearchParams();
  const filters = useMemo(() => parseMeetingFilters(sp), [sp]);
  const setFilters = (next: MeetingFilters) => setSp(meetingFiltersToSearchParams(next), { replace: true });
  const patch = (p: Partial<MeetingFilters>) => setFilters(updateMeetingFilters(filters, p));

  const apiQuery = meetingFiltersToApiQuery(filters, { scope });
  const query = useQuery({
    queryKey: ['meetings', apiQuery],
    queryFn: () => meetingsApi.list(apiQuery),
    placeholderData: keepPreviousData,
  });

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<unknown>(null);
  const exportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await meetingsApi.exportCsv(meetingFiltersToApiQuery(filters, { includePaging: false }));
    } catch (err) {
      setExportError(err);
    } finally {
      setExporting(false);
    }
  };

  const sortTh = (key: MeetingSortKey, label: string, className?: string) => (
    <SortableTh
      label={label}
      active={filters.sort === key}
      order={filters.order}
      onSort={() => setFilters(toggleSort(filters, key))}
      className={className}
    />
  );

  return (
    <div>
      <PageHeader
        title={scope === 'mine' ? 'My meetings' : 'Meetings'}
        description={scope === 'mine' ? 'Calls you recorded with the desktop app.' : undefined}
        actions={
          scope === 'visible' &&
          user.role === 'admin' && (
            <Button onClick={() => void exportCsv()} loading={exporting}>
              Export CSV
            </Button>
          )
        }
      />
      <ErrorBanner error={exportError} className="mb-3" />

      <FilterBar filters={filters} patch={patch} onClear={() => setFilters(clearMeetingFilters(filters))} showPeople={scope === 'visible'} />

      <Card bodyClassName="p-0" className="mt-4">
        {query.isError && <ErrorBanner error={query.error} onRetry={() => void query.refetch()} className="m-3" />}
        {query.isLoading ? (
          <ListSkeleton />
        ) : query.data && query.data.items.length === 0 ? (
          <EmptyState title={hasActiveFilters(filters) ? 'No meetings match these filters' : 'No meetings yet'}>
            {hasActiveFilters(filters)
              ? 'Try widening the date range or clearing filters.'
              : 'Meetings appear here after a call is recorded with the desktop app.'}
          </EmptyState>
        ) : query.data ? (
          <div className={cn(query.isPlaceholderData && 'opacity-60')}>
            <Table>
              <thead>
                <tr>
                  {sortTh('startedAt', 'Meeting')}
                  {sortTh('client', 'Client')}
                  {scope === 'visible' && sortTh('rep', 'Rep')}
                  <Th>Sentiment</Th>
                  <Th>Outcome</Th>
                  {sortTh('durationSec', 'Length', 'text-right')}
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((m, i) => (
                  <MeetingRow key={m.id} m={m} index={i} showRep={scope === 'visible'} />
                ))}
              </tbody>
            </Table>
            <div className="flex flex-wrap items-center justify-between border-t border-line">
              <Pagination page={filters.page} pageSize={filters.pageSize} total={query.data.total} onPage={(page) => patch({ page })} />
              <label className="flex items-center gap-2 px-3 py-2 text-xs text-muted">
                Rows
                <Select className="h-7 w-20" value={filters.pageSize} onChange={(e) => patch({ pageSize: Number(e.target.value) })}>
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </label>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function MeetingRow({ m, index, showRep }: { m: MeetingListItemDTO; index: number; showRep: boolean }) {
  return (
    <Tr className="rise" style={{ '--i': index } as CSSProperties}>
      <Td className="w-[38%] max-w-0">
        <Link to={`/meetings/${m.id}`} className="font-medium text-ink hover:text-accent-ink hover:underline">
          {m.title || 'Untitled meeting'}
        </Link>
        {m.status === 'live' && (
          <span className="ml-2 align-middle">
            <MeetingStatusBadge status={m.status} />
          </span>
        )}
        <div className="truncate text-xs text-muted">
          {formatDateTime(m.startedAt)}
          {m.headline ? ` · ${m.headline}` : ''}
        </div>
        {m.tags.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {m.tags.map((t) => (
              <Badge key={t}>{t}</Badge>
            ))}
          </div>
        )}
      </Td>
      <Td className="whitespace-nowrap">
        {m.clientName ? (
          <span className="inline-flex items-center gap-2">
            <Avatar name={m.clientName} />
            {m.clientName}
          </span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </Td>
      {showRep && (
        <Td className="whitespace-nowrap">
          {m.repName}
          {m.teamName && <div className="text-xs text-muted">{m.teamName}</div>}
        </Td>
      )}
      <Td>
        <SentimentBadge level={m.sentiment} />
      </Td>
      <Td>
        <OutcomeBadge outcome={m.outcome} />
      </Td>
      <Td className="text-right whitespace-nowrap tabular-nums">{formatDuration(m.durationSec)}</Td>
    </Tr>
  );
}

function FilterBar({
  filters,
  patch,
  onClear,
  showPeople,
}: {
  filters: MeetingFilters;
  patch: (p: Partial<MeetingFilters>) => void;
  onClear: () => void;
  showPeople: boolean;
}) {
  const user = useUser();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(filters.q ?? '');
  const debouncedQ = useDebounced(q, 350);

  // Push the debounced search text into the URL.
  useEffect(() => {
    const next = debouncedQ.trim() || undefined;
    if (next !== filters.q) patch({ q: next });
  }, [debouncedQ]); // only react to typing, not to other filter changes
  // Keep the box in sync when the URL changes elsewhere (back button, "Clear").
  useEffect(() => {
    if ((filters.q ?? '') !== debouncedQ.trim()) setQ(filters.q ?? '');
  }, [filters.q]);

  const users = useQuery({ queryKey: ['users'], queryFn: usersApi.list, enabled: showPeople && user.role !== 'rep' });
  const teams = useQuery({ queryKey: ['teams'], queryFn: teamsApi.list, enabled: showPeople && user.role === 'admin' });
  const selectedClient = useQuery({
    queryKey: ['client', filters.clientId],
    queryFn: () => clientsApi.get(filters.clientId!),
    enabled: !!filters.clientId && user.role !== 'rep',
  });

  const chips: Array<{ key: keyof MeetingFilters; label: string }> = [];
  if (filters.repId) chips.push({ key: 'repId', label: `Rep: ${users.data?.find((u) => u.id === filters.repId)?.name ?? 'selected'}` });
  if (filters.teamId) chips.push({ key: 'teamId', label: `Team: ${teams.data?.find((t) => t.id === filters.teamId)?.name ?? 'selected'}` });
  if (filters.clientId) chips.push({ key: 'clientId', label: `Client: ${selectedClient.data?.name ?? 'selected'}` });
  if (filters.platform) chips.push({ key: 'platform', label: `Platform: ${PLATFORM_LABELS[filters.platform]}` });
  if (filters.outcome) chips.push({ key: 'outcome', label: `Outcome: ${OUTCOME_LABELS[filters.outcome]}` });
  if (filters.sentiment) chips.push({ key: 'sentiment', label: `Sentiment: ${SENTIMENT_LABELS[filters.sentiment]}` });
  if (filters.status) chips.push({ key: 'status', label: `Status: ${STATUS_LABELS[filters.status]}` });

  return (
    <div className="space-y-3 rounded-card border border-line bg-surface p-4 shadow-card">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Search summaries & transcripts" className="min-w-60 flex-[2]">
          <Input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. pricing, SOC 2, onboarding…" />
        </Field>
        <Field label="From" className="w-36">
          <Input type="date" value={filters.from ?? ''} max={filters.to} onChange={(e) => patch({ from: e.target.value || undefined })} />
        </Field>
        <Field label="To" className="w-36">
          <Input type="date" value={filters.to ?? ''} min={filters.from} onChange={(e) => patch({ to: e.target.value || undefined })} />
        </Field>
        <Button aria-expanded={open} aria-controls="meeting-filters" onClick={() => setOpen((o) => !o)}>
          <SlidersHorizontal aria-hidden className="h-4 w-4" />
          Filters{chips.length > 0 ? ` (${chips.length})` : ''}
        </Button>
      </div>
      {open && (
        <div id="meeting-filters" className="rise flex flex-wrap items-end gap-3">
          {showPeople && user.role !== 'rep' && (
            <Field label="Rep" className="w-44">
              <Select value={filters.repId ?? ''} onChange={(e) => patch({ repId: e.target.value || undefined })}>
                <option value="">All reps</option>
                {(users.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {showPeople && user.role === 'admin' && (
            <Field label="Team" className="w-40">
              <Select value={filters.teamId ?? ''} onChange={(e) => patch({ teamId: e.target.value || undefined })}>
                <option value="">All teams</option>
                {(teams.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {user.role !== 'rep' && (
            <Field label="Client" className="w-56">
              {filters.clientId ? (
                <div className="flex h-8 items-center justify-between gap-2 rounded-md border border-line bg-subtle px-2.5 text-sm">
                  <span className="truncate">{selectedClient.data?.name ?? 'Selected client'}</span>
                  <button type="button" className="text-muted hover:text-danger" aria-label="Clear client filter" onClick={() => patch({ clientId: undefined })}>
                    ×
                  </button>
                </div>
              ) : (
                <ClientPicker onSelect={(c) => patch({ clientId: c.id })} placeholder="Any client" />
              )}
            </Field>
          )}
          <Field label="Platform" className="w-36">
            <Select value={filters.platform ?? ''} onChange={(e) => patch({ platform: (e.target.value || undefined) as MeetingFilters['platform'] })}>
              <option value="">Any</option>
              {PlatformSchema.options.map((p) => (
                <option key={p} value={p}>
                  {PLATFORM_LABELS[p]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Outcome" className="w-44">
            <Select value={filters.outcome ?? ''} onChange={(e) => patch({ outcome: (e.target.value || undefined) as MeetingFilters['outcome'] })}>
              <option value="">Any</option>
              {OutcomeSchema.options.map((o) => (
                <option key={o} value={o}>
                  {OUTCOME_LABELS[o]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Sentiment" className="w-40">
            <Select value={filters.sentiment ?? ''} onChange={(e) => patch({ sentiment: (e.target.value || undefined) as MeetingFilters['sentiment'] })}>
              <option value="">Any</option>
              {SentimentLevelSchema.options.map((s) => (
                <option key={s} value={s}>
                  {SENTIMENT_LABELS[s]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status" className="w-32">
            <Select value={filters.status ?? ''} onChange={(e) => patch({ status: (e.target.value || undefined) as MeetingFilters['status'] })}>
              <option value="">Any</option>
              {MeetingStatusSchema.options.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}
      {(chips.length > 0 || hasActiveFilters(filters)) && (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <span key={c.key} className="inline-flex items-center gap-1 rounded-full bg-accent-soft py-0.5 pr-1 pl-2.5 text-xs font-medium text-accent-ink">
              {c.label}
              <button
                type="button"
                aria-label={`Remove filter ${c.label}`}
                onClick={() => patch({ [c.key]: undefined })}
                className="grid h-4 w-4 place-items-center rounded-full hover:bg-accent/15"
              >
                <X aria-hidden className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button type="button" onClick={onClear} className="text-xs text-muted hover:text-ink hover:underline">
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
